import type { MouseEvent } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { DownloadIcon, FileIcon, Trash2Icon } from "@lucide/icons";

import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent, CardGroup } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import { Segment } from "simplydo/react/ui/segment";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import util from "utils/utils";

import { FileChooserWithDropzone } from "components/lib/Choosers";

import { FieldProps, IdeaFileValue } from "./FieldProps";

type FileComponentProps = {
  files: IdeaFileValue[];
  onRemove?: (fileValue: string) => void;
};

export const FileComponent = ({ files, onRemove }: FileComponentProps) => {
  const { t } = useTranslation();

  return (
    <CardGroup onClick={(e: MouseEvent) => e.stopPropagation()}>
      <Card style={{ width: "80%" }}>
        <CardContent style={{ color: "#808080" }}>
          <Icon icon={FileIcon} />
          {util.pluralise(files.length, "uploaded file", "uploaded files")}
        </CardContent>
        <CardContent textAlign="center">
          <Table>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.value}>
                  <TableCell collapsing style={{ padding: 0 }}>
                    <Icon
                      icon={FileIcon}
                      size="large"
                      style={{ color: "#808080" }}
                    />
                  </TableCell>
                  <TableCell>
                    <DownloadLink url={file.value} name={file.name}>
                      {file.name}
                    </DownloadLink>
                  </TableCell>

                  <TableCell collapsing>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "0.25rem",
                      }}
                    >
                      <DownloadLink url={file.value} name={file.name}>
                        <Button
                          size="small"
                          style={{ marginLeft: 0, marginRight: 0 }}
                          variant="outline"
                        >
                          <Icon icon={DownloadIcon} />
                          {t("generic.download")}
                        </Button>
                      </DownloadLink>

                      {onRemove ? (
                        <Button
                          onClick={() => onRemove(file.value)}
                          size="small"
                          style={{ marginLeft: 0, marginRight: 0 }}
                          variant="outline"
                        >
                          <Icon icon={Trash2Icon} />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </CardGroup>
  );
};

const File = ({
  field,
  ideaId,
  fieldValue,
  updateIdea,
  isEditing,
}: FieldProps) => {
  const fileValue = fieldValue as IdeaFileValue | null;

  if (!isEditing) {
    if (fileValue) return <FileComponent files={[fileValue]} />;
    return null;
  }

  if (fileValue) {
    return (
      <Segment placeholder>
        <FileComponent
          files={[fileValue]}
          onRemove={() => updateIdea(null, ["templated", field.id])}
        />
      </Segment>
    );
  }

  return (
    <FileChooserWithDropzone
      forType="idea"
      forId={ideaId}
      onComplete={(_id: unknown, fileName: string, imageSrc: string) => {
        updateIdea({ value: imageSrc, name: fileName }, [
          "templated",
          field.id,
        ]);
      }}
      onError={toast.error}
      popupProps={{ position: "bottom center" }}
      dropzoneProps={{
        title: "Upload a file",
        DropZoneComponent: Segment,
        defaultIcon: FileIcon,
      }}
    />
  );
};

export default File;
